// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { ArticleTypeIds } from '@article/constants/article-type-ids';
import { ArticleCollectionVO, ArticleVO } from '@article/model/vo/article.vo';
import { ArticleDataService } from '@article/service/article-data.service';
import { AlertHelper } from '@shared/alert/alert.helper';
import { Log } from '@shared/debug/log';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { lastValueFrom } from 'rxjs';
import { UserModel } from '../model/user.model';
import { ChoiceActionSettingComponent } from './choice-action-setting/choice-action-setting.component';
import { AddArticleToCollectionMode } from './favorites/components/add-to-collection/add-article-to-collection-mode.enum';
import { AddArticleToCollectionComponent } from './favorites/components/add-to-collection/add-article-to-collection.component';
import { EditArticleCollectionComponent } from './favorites/components/edit-article-collection/edit-article-collection.component';
import { FavoriteArticlesOrderNavigatorContent } from './favorites/components/favorite-articles-order/favorite-articles-order-navigator-content';
import { FavoriteArticlesOrderComponent } from './favorites/components/favorite-articles-order/favorite-articles-order.component';
import { FavoriteArticlesWrapperVO } from './favorites/models/favorite-article.vo';
import { LanguageSelectionComponent } from './language/language-selection.component';
import { PermissionsUserComponent } from './permissions-user/permissions-user.component';
import { PermissionsModalOptions } from './permissions-user/permissions-user.model';
import { ReloginActionComponent } from './relogin-action/relogin-action.component';
import { ReloginComponent } from './relogin/relogin.component';
import { UserRightsManagementComponent } from './user-rights/user-rights-management.component';
import { WindowService } from '@shared/window/window.service';
import { LocaleLanguage } from './language/locale-language';

@Injectable({ providedIn: 'root' })
export class UserComponentPresenter {
  constructor(
    private _alertHelper: AlertHelper,
    private articleDataService: ArticleDataService,
    private _userModel: UserModel,
    private _localeResourceManager: LocaleResourceManager,
    private windowService: WindowService
  ) {}

  public presentReloginActionComponent(titleKey: string, infoMessageKey: string): Promise<void> {
    return this.windowService.showModalFinish(ReloginActionComponent, {
      initialState: {
        titleKey,
        infoMessageKey,
      },
    });
  }

  public presentChoiceActionSettingComponent(): void {
    this.windowService.showModal(ChoiceActionSettingComponent, {
      keyboard: true,
    });
  }

  public presentAddArticleToArticleCollectionPopup(article: ArticleVO, articleCollections: ArticleCollectionVO[]): Promise<ArticleCollectionVO | null> {
    return this.windowService.showModalFinish(AddArticleToCollectionComponent, {
      initialState: {
        mode: AddArticleToCollectionMode.SINGLE,
        articleCollections,
        articles: [article],
      },
      keyboard: true,
    });
  }

  public presentAddMultipleArticlesToArticleCollectionPopup(articles: ArticleVO[], articleCollections: ArticleCollectionVO[]): Promise<ArticleCollectionVO | null> {
    return this.windowService.showModalFinish(AddArticleToCollectionComponent, {
      initialState: {
        mode: AddArticleToCollectionMode.MULTIPLE,
        articleCollections: articleCollections,
        articles: articles,
        displayAllArticleNos: true,
      },
      keyboard: true,
      ignoreBackdropClick: true,
    });
  }

  public presentEditArticleCollectionComponent(articleCollection: ArticleCollectionVO, collectionArticles: ArticleVO[] = []): Promise<ArticleCollectionVO | null> {
    return this.windowService.showModalFinish(EditArticleCollectionComponent, {
      initialState: {articleCollection, collectionArticles},
      keyboard: true,
      ignoreBackdropClick: true,
    });
  }

  public async presentFavoriteArticlesOrderComponent(): Promise<void> {
    const favoriteTypeIds: number[] = [];
    favoriteTypeIds.push(ArticleTypeIds.LABELING);
    favoriteTypeIds.push(ArticleTypeIds.BRIDGE);
    favoriteTypeIds.push(ArticleTypeIds.MOUNTING_RAIL);

    try {
      const favoriteArticleWrappers = await lastValueFrom(this.articleDataService.getFavoriteArticles(favoriteTypeIds));
      const data: FavoriteArticlesOrderNavigatorContent[] = this.getArticlesContent(favoriteArticleWrappers);
      this.windowService.showModal(FavoriteArticlesOrderComponent, {
        class: `max-width-900`,
        initialState: { data: data },
        keyboard: true,
        ignoreBackdropClick: true,
      });
    } catch (error) {
      Log.error('Could not load favorite articles: ', error);
      await this.showFavoriteArticleErrorAlert();
    }
  }

  private getArticlesContent(favoriteArticleWrappers: FavoriteArticlesWrapperVO[]): FavoriteArticlesOrderNavigatorContent[] {
    const navigatorContents: FavoriteArticlesOrderNavigatorContent[] = [];
    for (const wrapper of favoriteArticleWrappers) {
      let navigatorContent: FavoriteArticlesOrderNavigatorContent;
      switch (wrapper.articleTypeId) {
        case ArticleTypeIds.LABELING:
          navigatorContent = new FavoriteArticlesOrderNavigatorContent(
            this._localeResourceManager,
            this._userModel,
            true,
            false,
            wrapper.favoriteArticles
          );
          navigatorContent.tabHeaderText = 'favoriteArticlesOrderPopup.tab_label.labeling_materials';
          break;
        case ArticleTypeIds.BRIDGE:
          navigatorContent = new FavoriteArticlesOrderNavigatorContent(
            this._localeResourceManager,
            this._userModel,
            true,
            false,
            wrapper.favoriteArticles
          );
          navigatorContent.tabHeaderText = wrapper.isWireBridgeArticles
            ? 'favoriteArticlesOrderPopup.tab_label.wire_bridges'
            : 'favoriteArticlesOrderPopup.tab_label.bridges';
          break;
        case ArticleTypeIds.MOUNTING_RAIL:
          navigatorContent = new FavoriteArticlesOrderNavigatorContent(
            this._localeResourceManager,
            this._userModel,
            true,
            false,
            wrapper.favoriteArticles
          );
          navigatorContent.tabHeaderText = 'favoriteArticlesOrderPopup.tab_label.mounting_rails';
          break;
        default:
          break;
      }
      if (navigatorContents) {
        navigatorContents.push(navigatorContent);
      }
    }
    return navigatorContents;
  }

  private async showFavoriteArticleErrorAlert(): Promise<void> {
    await this._alertHelper.showError(
      'favoriteArticlesOrderPopup.error.title.load_favorite_articles',
      'favoriteArticlesOrderPopup.error.message.load_favorite_articles'
    );
  }

  public presentLanguageComponent(langKey: string = null): Promise<LocaleLanguage | null> {
    return this.windowService.showModalFinish(LanguageSelectionComponent, {
      class: 'modal-sm',
      initialState: {
        langKey,
      },
      keyboard: true,
      ignoreBackdropClick: true,
    });
  }

  public presentPermissionsUserComponent(modalOptions: PermissionsModalOptions): void {
    this.windowService.showModal(PermissionsUserComponent, {
      class: 'modal-fz',
      initialState: modalOptions,
    });
  }

  public async presentReloginComponent(showHeaderLogin: boolean = true, closable: boolean = true, onTop: boolean = false): Promise<void> {
    if (onTop) {
      this.focusLastModal();
    }
    await this.windowService.showModalFinish(ReloginComponent, {
      initialState: {
        showHeaderLogin,
        closable,
      },
      keyboard: closable,
      ignoreBackdropClick: !closable,
    });
  }

  public openUserRightsManagementDialog(): void {
    this.windowService.showModal(UserRightsManagementComponent, {
      class: 'modal-lg',
      keyboard: true,
      ignoreBackdropClick: true,
    });
  }

  private focusLastModal(): void {
    setTimeout(() => {
      const collection = document.getElementsByTagName('modal-container');
      (<HTMLElement>collection[collection.length - 1]).classList.add('modal-top');
    }, 0);
  }
}
